<template>
    <div v-if="isPageAccessible" class="content-wrapper">
        <Breadcrumbs :items="breadcrumbs"></Breadcrumbs>
        <div class="content">
            <div class="starter-pack-page">
                <StarterPackLoader v-if="pageLoading" label="Загружаем данные..." />

                <template v-else>
                    <StarterPackHeader
                        :active-count="activeCount"
                        :total-count="totalCount"
                        :progress-pct="progressPct"
                    />

                    <div class="starter-pack-dynamic">
                        <StarterPackDoctorMetrics :metrics="doctorMetrics" />

                        <StarterPackScenarioSections
                            :sections="scenarioSections"
                            :role-labels="roleLabels"
                            :launching="launching"
                            :can-save-settings="canSaveStarterPackSettings"
                            :all-scenarios="visibleScenarios"
                            @configure="configureScenario"
                            @launch="launchScenario"
                            @disable="disableScenario"
                            @locked-click="showChannelGateModal = true"
                        />

                        <StarterPackLoader
                            v-if="scenariosRefreshing"
                            overlay
                            label="Обновляем статус..."
                        />
                    </div>

                    <StarterPackInfoCards :cards="infoCards" @learn="openInfoCardLearn" />

                    <StarterPackResultsSection :success-cases="successCases" :reviews="reviews" />
                </template>
            </div>
        </div>

        <StarterPackSettingsDrawer
            :scenario="drawerScenario"
            :deauth-loading="deauthLoading"
            :services-loading="master15ServicesLoading"
            :launching="launching"
            :client-notifications-triggers-url="clientNotificationsTriggersUrl"
            :redirect-url="drawerRedirectUrl"
            :phone-validator="phoneValidator"
            :deauth-contacts-value="deauthContactsValue"
            :deauth-contacts="deauthContacts"
            :deauth-all-groups="deauthAllGroups"
            :deauth-access-error-message="deauthAccessErrorMessage"
            :can-manage-deauth-recipients="canManageDeauthRecipients"
            :is-super-user="isSuperUser"
            :update-deauth-contacts-value="updateDeauthContactsValue"
            :remove-deauth-contact="removeDeauthContact"
            :verify-deauth-contact="verifyDeauthContact"
            :deauth-verifying-indices="deauthVerifyingIndices"
            :send-to-report-recipients="sendToReportRecipients"
            :update-send-to-report-recipients="updateSendToReportRecipients"
            :comm-channels="commChannels"
            :selected-comm-channel-id="selectedCommChannelId"
            :selected-comm-channel="selectedCommChannel"
            :comm-help-url="commHelpUrl"
            :service-list="serviceList"
            :project-id="getProjectId()"
            :can-save-settings="canSaveStarterPackSettings"
            @close="closeDrawer"
            @launch="launchScenarioFromDrawer"
            @select-comm-channel="selectCommChannel"
            @phones-updated="onCommPhonesUpdated"
            @cascade-readiness-change="handleCascadeReadinessChange"
            @toast="showToast"
        />

        <StarterPackToast :message="toastMessage" />

        <StarterPackChannelGateModal
            :open="showChannelGateModal"
            @close="showChannelGateModal = false"
            @connect="openCascadeGateDrawer"
        />

        <StarterPackActivationScriptModal
            :scenario="scriptModalScenario"
            :staff="staff"
            :launching="launching"
            :can-save-settings="canSaveStarterPackSettings"
            :open-employee-menu-scenario-id="openEmployeeMenuScenarioId"
            @close="closeScriptModal"
            @launch="applyScriptModal"
            @toggle-employee-menu="toggleEmployeeMenu(scriptModalScenario && scriptModalScenario.scenarioId)"
            @pick-employee="pickEmployee(scriptModalScenario, $event)"
            @remove-master="removeMaster(scriptModalScenario, $event)"
        />

        <StarterPackDisableConfirmModal
            :open="Boolean(disableConfirmScenario)"
            :message="disableConfirmMessage"
            :disabling="launching"
            @close="closeDisableConfirmModal"
            @confirm="confirmDisableScenario"
        />
    </div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import deauthNotificationRecipientsMixin from '@/components/ClientNotifications/deauthNotificationRecipientsMixin.js'
import phoneValidator from '@/helpers/validators/phoneValidator'
import { getServiceList, getStaffList, communicationUpdate } from '@/api/chisai/GCB2'
import { controlPhoneList, editAdditionalServices, getAdditionalServices } from '@/api/networkFixer'
import {
    STARTER_PACK_META,
    getMockStarterPackStatus,
    getStarterPackStatus,
    hardcodedReviews,
    hardcodedSuccessCases,
    launchStarterPackScenarios,
    disableStarterPackScenario,
    mapDiagnosticsToDoctorMetrics,
    mockInfoCards,
    mockStaff,
} from '../api'
import { COMM_HELP_URL, DH_APP_URL, ROLE_LABELS, CASCADE_GATE_SCENARIO_ID, STARTER_PACK_DISABLE_CONFIRM_MESSAGES } from '../constants'
import { buildCommChannels } from '../utils/commChannels'
import { buildLaunchSettings, normalizeSettings } from '../utils/scenarioSettings'
import { showConfigureButton, isScenarioActive, isScenarioCascadeLocked } from '../utils/scenarioUi'
import { shouldReloadCascadeScenarioStatus } from '../utils/cascadeDrawerSync'
import { filterVisibleStarterPackScenarios, isStarterPackScenarioVisible } from '../utils/scenarioVisibility'
import StarterPackHeader from '../components/StarterPackHeader.vue'
import StarterPackDoctorMetrics from '../components/StarterPackDoctorMetrics.vue'
import StarterPackScenarioSections from '../components/StarterPackScenarioSections.vue'
import StarterPackInfoCards from '../components/StarterPackInfoCards.vue'
import StarterPackResultsSection from '../components/StarterPackResultsSection.vue'
import StarterPackSettingsDrawer from '../components/StarterPackSettingsDrawer.vue'
import StarterPackChannelGateModal from '../components/StarterPackChannelGateModal.vue'
import StarterPackActivationScriptModal from '../components/StarterPackActivationScriptModal.vue'
import StarterPackDisableConfirmModal from '../components/StarterPackDisableConfirmModal.vue'
import StarterPackToast from '../components/StarterPackToast.vue'
import StarterPackLoader from '../components/StarterPackLoader.vue'
import cfg from '@root/config.js'
import { ensureProjectBillingLoaded } from '@/helpers/channels/ensureProjectBillingLoaded'
import { checkRole } from '@/helpers/roles'
import { CONNECTOR_TYPES } from '@root/src/vars/general'
import { useBillingStore } from '@/store/stores/billing'

export default {
    name: 'StarterPack',
    mixins: [deauthNotificationRecipientsMixin],
    setup() {
        const billingStore = useBillingStore()
        return { billingStore }
    },
    components: {
        Breadcrumbs,
        StarterPackHeader,
        StarterPackDoctorMetrics,
        StarterPackScenarioSections,
        StarterPackInfoCards,
        StarterPackResultsSection,
        StarterPackSettingsDrawer,
        StarterPackChannelGateModal,
        StarterPackActivationScriptModal,
        StarterPackDisableConfirmModal,
        StarterPackToast,
        StarterPackLoader,
    },
    props: {
        id: {
            type: [String, Number],
            default: '',
        },
    },
    data() {
        return {
            phoneValidator,
            scenarios: [],
            doctorMetrics: [],
            infoCards: mockInfoCards,
            serviceList: [],
            startAdditionalServices: [],
            staff: mockStaff,
            successCases: hardcodedSuccessCases,
            reviews: hardcodedReviews,
            openScriptScenarioId: null,
            openEmployeeMenuScenarioId: null,
            drawerScenarioId: null,
            launching: false,
            toastMessage: '',
            toastTimer: null,
            commControlPhonesByConnector: {},
            selectedCommChannelId: null,
            roleLabels: ROLE_LABELS,
            showChannelGateModal: false,
            disableConfirmScenario: null,
            isPageAccessible: false,
            pageLoading: true,
            scenariosRefreshing: false,
            deauthLoading: false,
            master15ServicesLoading: false,
        }
    },
    computed: {
        project() {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id) || {}
        },
        currentTariff() {
            const projectId = this.getProjectId()
            if (!projectId) {
                return null
            }
            return this.billingStore.getCurrentTariff(projectId)
        },
        yClientsConnector() {
            return (this.project?.connectors || []).find(
                connector => connector.connectorType === CONNECTOR_TYPES.yClients,
            )
        },
        canAccessStarterPack() {
            const projectId = this.getProjectId()
            if (!projectId) {
                return false
            }
            return (
                checkRole(projectId, 'project:settings:view')
            )
        },
        canSaveStarterPackSettings() {
            const projectId = this.getProjectId()
            if (!projectId) {
                return false
            }
            return checkRole(projectId, 'project:settings:save')
        },
        commChannels() {
            return buildCommChannels({
                connectors: this.project?.connectors || [],
                commControlPhonesByConnector: this.commControlPhonesByConnector,
                connectorsAvatars: this.$store.state.connectorsAvatars,
            })
        },
        selectedCommChannel() {
            return this.commChannels.find(channel => channel.id === this.selectedCommChannelId) || null
        },
        commHelpUrl() {
            return COMM_HELP_URL
        },
        clientNotificationsTriggersUrl() {
            const projectId = this.id || this.$router.currentRoute.params.id || this.project?.id
            return `${cfg.frontendEndpoint}/project/${projectId}/clientNotifications?section=notifications`
        },
        breadcrumbs() {
            return [
                {
                    text: this.project?.name || 'Проект',
                    to: `/project/${this.$router.currentRoute.params.id}`,
                },
                { text: 'Старт в DH', to: '', disabled: true },
            ]
        },
        totalCount() {
            return this.scenarios.length
        },
        activeCount() {
            return this.scenarios.filter(scenario => scenario.status === 'active').length
        },
        progressPct() {
            return this.totalCount ? Math.round((this.activeCount / this.totalCount) * 100) : 0
        },
        visibleScenarios() {
            return filterVisibleStarterPackScenarios(this.scenarios, Boolean(this.yClientsConnector))
        },
        scenarioSections() {
            return [
                { id: 'must', title: 'Необходимое', scenarios: this.mustScenarios },
                {
                    id: 'quickstart-admin',
                    title: 'Быстрый старт',
                    subtitle: 'Для администратора',
                    subtitleClass: 'admin',
                    tight: true,
                    scenarios: this.quickstartAdminScenarios,
                },
                {
                    id: 'quickstart-master',
                    subtitle: 'Для мастера',
                    subtitleClass: 'master',
                    scenarios: this.quickstartMasterScenarios,
                },
            ]
        },
        mustScenarios() {
            return this.visibleScenarios.filter(scenario => scenario.section === 'must')
        },
        quickstartAdminScenarios() {
            return this.visibleScenarios.filter(scenario => scenario.section === 'quickstart-admin')
        },
        quickstartMasterScenarios() {
            return this.visibleScenarios.filter(scenario => scenario.section === 'quickstart-master')
        },
        drawerScenario() {
            return this.scenarios.find(scenario => scenario.scenarioId === this.drawerScenarioId)
        },
        scriptModalScenario() {
            return this.scenarios.find(scenario => scenario.scenarioId === this.openScriptScenarioId) || null
        },
        disableConfirmMessage() {
            const action = this.disableConfirmScenario?.action
            return STARTER_PACK_DISABLE_CONFIRM_MESSAGES[action] || ''
        },
        drawerRedirectUrl() {
            const scenario = this.drawerScenario
            if (!scenario) {
                return DH_APP_URL
            }
            const projectId = this.getProjectId()
            if (scenario.meta.redirectAnchor) {
                return `${cfg.frontendEndpoint}/project/${projectId}#${scenario.meta.redirectAnchor}`
            }
            if (scenario.meta.redirectPath) {
                return `${cfg.frontendEndpoint}/project/${projectId}/${scenario.meta.redirectPath}`
            }
            return scenario.meta.redirectUrl || DH_APP_URL
        },
    },
    async created() {
        this.onEmployeeMenuOutsideClick = event => {
            if (this.openEmployeeMenuScenarioId === null) {
                return
            }
            if (!event.target.closest('.emp')) {
                this.closeEmployeeMenu()
            }
        }
        document.addEventListener('click', this.onEmployeeMenuOutsideClick)
        window.addEventListener('focus', this.handleWindowFocus)
        await this.ensureProjectBillingLoaded()
        if (!this.canAccessStarterPack) {
            this.$router.replace(`/project/${this.getProjectId()}`)
            return
        }
        this.isPageAccessible = true
        try {
            await Promise.all([this.loadScenarios(), this.loadStaffList()])
        } finally {
            this.pageLoading = false
        }
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onEmployeeMenuOutsideClick)
        window.removeEventListener('focus', this.handleWindowFocus)
        clearTimeout(this.toastTimer)
    },
    methods: {
        handleWindowFocus() {
            if (this.isPageAccessible && !this.pageLoading) {
                void this.loadScenarios({ fallbackToMock: false })
            }
        },
        handleCascadeReadinessChange(isReady) {
            if (!shouldReloadCascadeScenarioStatus(isScenarioActive(this.drawerScenario), isReady)) {
                return
            }

            if (this.isPageAccessible && !this.pageLoading) {
                void this.loadScenarios({ fallbackToMock: false })
            }
        },
        getProjectId() {
            return String(this.id || this.$router.currentRoute.params.id || this.project?.id || '')
        },
        async ensureProjectBillingLoaded() {
            const projectId = this.getProjectId()
            const project = this.$store.getters.projectById(projectId) || this.project
            await ensureProjectBillingLoaded(projectId, project)
        },
        async loadScenarios({ fallbackToMock = true } = {}) {
            const isRefresh = this.scenarios.length > 0
            if (isRefresh) {
                this.scenariosRefreshing = true
            }

            try {
                const projectId = this.getProjectId()
                const { data, error } = projectId
                    ? await getStarterPackStatus({ projectId })
                    : { data: null, error: true }

                let scenarios = data?.scenarios
                let diagnostics = data?.diagnostics
                if (error || !scenarios?.length) {
                    if (!fallbackToMock) {
                        this.$store.dispatch('callNotify', 'Не удалось обновить статус сценариев')
                        return
                    }
                    const mockResponse = await getMockStarterPackStatus()
                    scenarios = mockResponse.scenarios
                    diagnostics = undefined
                }

                this.doctorMetrics = mapDiagnosticsToDoctorMetrics(diagnostics)
                this.scenarios = scenarios.map(scenario => {
                    const meta = STARTER_PACK_META.find(item => item.scenarioId === scenario.scenarioId) || {}
                    return {
                        ...scenario,
                        meta,
                        settings: normalizeSettings(scenario),
                    }
                })
            } finally {
                if (isRefresh) {
                    this.scenariosRefreshing = false
                }
            }
        },
        async loadStaffList() {
            const projectId = this.getProjectId()
            if (!projectId) {
                this.staff = mockStaff
                return
            }

            const { data, error } = await getStaffList({ projectId })
            if (error || !Array.isArray(data) || data.length === 0) {
                this.staff = mockStaff
                return
            }

            const staff = data
                .map(item => ({
                    id: String(item.id ?? item.staff_id ?? item.staffId ?? ''),
                    name: String(item.name ?? item.staff_name ?? item.staffName ?? '').trim(),
                }))
                .filter(item => item.id && item.name)

            this.staff = staff.length > 0 ? staff : mockStaff
        },
        openInfoCardLearn(card) {
            const projectId = this.getProjectId()

            if (card.marketplaceModule) {
                const marketplaceId = cfg.marketplaceModulesIds[cfg.buildType][card.marketplaceModule]
                if (marketplaceId && projectId) {
                    const url = `${cfg.frontendEndpoint}/project/${projectId}/marketplace/integration/${marketplaceId}`
                    window.open(url, '_blank', 'noopener')
                    return
                }
            }

            if (!card.learnPath) {
                return
            }
            const url = `${cfg.frontendEndpoint}/project/${projectId}/${card.learnPath}`
            window.open(url, '_blank', 'noopener')
        },
        async configureScenario(scenario) {
            if (!this.guardCascadeGate(scenario)) {
                return
            }
            if (scenario.meta.settingsType === 'redirect-info' || scenario.meta.settingsType === 'cascade') {
                this.drawerScenarioId = scenario.scenarioId
                return
            }
            if (scenario.action === 'redirect') {
                const projectId = this.id || this.$router.currentRoute.params.id || this.project?.id
                if (scenario.meta.redirectAnchor) {
                    const url = `${cfg.frontendEndpoint}/project/${projectId}#${scenario.meta.redirectAnchor}`
                    window.open(url, '_blank', 'noopener')
                    this.showToast('Открываем DH')
                    return
                }
                if (scenario.meta.redirectPath) {
                    const url = `${cfg.frontendEndpoint}/project/${projectId}/${scenario.meta.redirectPath}`
                    window.open(url, '_blank', 'noopener')
                    this.showToast('Открываем DH')
                    return
                }
                window.open(scenario.meta.redirectUrl || DH_APP_URL, '_blank')
                this.showToast('Открываем DH')
                return
            }
            if (scenario.meta.settingsType === 'script') {
                this.openScriptScenarioId =
                    this.openScriptScenarioId === scenario.scenarioId ? null : scenario.scenarioId
                return
            }

            this.drawerScenarioId = scenario.scenarioId

            const settingsType = scenario.meta.settingsType
            if (settingsType === 'comm') {
                this.ensureSelectedCommChannel()
                return
            }
            if (settingsType === 'deauth') {
                this.deauthLoading = true
                this.loadDeauthNotificationRecipients().finally(() => {
                    if (this.drawerScenario?.meta?.settingsType === 'deauth') {
                        this.deauthLoading = false
                    }
                })
                return
            }
            if (settingsType === 'master15') {
                this.master15ServicesLoading = true
                this.loadMaster15Services().finally(() => {
                    if (this.drawerScenario?.meta?.settingsType === 'master15') {
                        this.master15ServicesLoading = false
                    }
                })
            }
        },
        async loadMaster15Services() {
            const projectId = this.project?.id
            if (!projectId) {
                return
            }

            const { data, error } = await getServiceList({ projectId })
            if (error) {
                this.$store.dispatch('callNotify', 'При загрузке данных произошла ошибка')
                return
            }

            this.serviceList = data || []

            const resAdditionalServices = await getAdditionalServices({ projectId })
            if (resAdditionalServices?.error) {
                this.$store.dispatch('callNotify', 'При загрузке данных произошла ошибка')
                return
            }

            const selectedServices = this.serviceList.filter(el =>
                resAdditionalServices.data?.additionalServices?.some(item => item.service_id == el.id),
            )
            this.startAdditionalServices = selectedServices.map(service => ({
                id: service.id,
                name: service.name,
            }))

            if (this.drawerScenario) {
                this.drawerScenario.settings.additionalServiceIds = selectedServices.map(service => service.id)
            }
        },
        async saveAdditionalServicesChanges() {
            const projectId = this.project?.id
            if (!projectId || !this.drawerScenario) {
                return true
            }

            const selectedIds = this.drawerScenario.settings.additionalServiceIds || []
            const startIds = this.startAdditionalServices.map(service => service.id)
            const addedIds = selectedIds.filter(id => !startIds.some(startId => startId == id))
            const deletedServices = this.startAdditionalServices.filter(
                service => !selectedIds.some(id => id == service.id),
            )

            const servicesToSave = []
            addedIds.forEach(id => {
                const service = this.serviceList.find(item => item.id == id)
                if (service) {
                    servicesToSave.push({
                        id: Number(service.id) ? Number(service.id) : service.id,
                        name: service.name,
                        type: 'add',
                    })
                }
            })
            deletedServices.forEach(service => {
                servicesToSave.push({
                    id: Number(service.id) ? Number(service.id) : service.id,
                    name: service.name,
                    type: 'remove',
                })
            })

            if (servicesToSave.length === 0) {
                return true
            }

            const { error } = await editAdditionalServices({ projectId, services: servicesToSave })
            if (error) {
                this.$store.dispatch('callNotify', 'При сохранении данных произошла ошибка')
                return false
            }

            this.startAdditionalServices = this.serviceList
                .filter(service => selectedIds.some(id => id == service.id))
                .map(service => ({ id: service.id, name: service.name }))

            return true
        },
        async launchScenarioFromScript(scenario) {
            if (!this.guardCascadeGate(scenario)) {
                return
            }
            this.openScriptScenarioId = null
            await this.launchSingleScenario(scenario)
        },
        async applyScriptModal() {
            const scenario = this.scriptModalScenario
            if (!scenario) {
                return
            }
            await this.launchScenarioFromScript(scenario)
        },
        closeScriptModal() {
            if (this.launching) {
                return
            }
            this.openScriptScenarioId = null
            this.closeEmployeeMenu()
        },
        async launchScenarioFromDrawer() {
            const scenario = this.drawerScenario
            if (!scenario) {
                return
            }

            if (!this.guardCascadeGate(scenario)) {
                return
            }

            if (scenario.meta?.settingsType === 'master15') {
                const saved = await this.saveAdditionalServicesChanges()
                if (!saved) {
                    return
                }
            }
            if (scenario.meta?.settingsType === 'deauth') {
                scenario.settings = {
                    contacts: (this.deauthContacts || [])
                        .filter(contact => String(contact?.contactValue ?? '').trim())
                        .map(contact => ({
                            phone: String(contact.contactValue ?? '').trim(),
                            isVerified: contact.isVerified ?? undefined,
                            displayName: contact.displayName ?? undefined,
                        })),
                    sendToReportRecipients: Boolean(this.sendToReportRecipients),
                }
            }
            if (scenario.meta?.settingsType === 'comm') {
                scenario.settings = {
                    phones: this.collectAllCommPhonesForSettings(),
                }
            }

            const launched = await this.launchSingleScenario(scenario)
            if (launched) {
                this.closeDrawer()
            }
        },
        async launchScenario(scenario) {
            if (!this.guardCascadeGate(scenario)) {
                return
            }
            if (isScenarioActive(scenario) || showConfigureButton(scenario)) {
                return
            }
            await this.launchSingleScenario(scenario)
        },
        guardCascadeGate(scenario) {
            if (!isScenarioCascadeLocked(scenario, this.scenarios)) {
                return true
            }
            this.showChannelGateModal = true
            return false
        },
        openCascadeGateDrawer() {
            this.showChannelGateModal = false
            const scenario = this.scenarios.find(item => item.scenarioId === CASCADE_GATE_SCENARIO_ID)
            if (!scenario) {
                return
            }
            this.drawerScenarioId = scenario.scenarioId
        },
        async launchSingleScenario(scenario) {
            if (!isStarterPackScenarioVisible(scenario, Boolean(this.yClientsConnector))) {
                return false
            }

            const projectId = this.getProjectId()
            if (!projectId) {
                return false
            }

            this.launching = true
            const { data, error } = await launchStarterPackScenarios({
                projectId,
                scenarioIds: [scenario.scenarioId],
                settings: buildLaunchSettings([scenario]),
            })
            this.launching = false

            if (error || !data) {
                this.showToast('Ошибка запуска сценария')
                return false
            }

            const result = data.results.find(item => item.scenarioId === scenario.scenarioId)
            if (!result || !result.success) {
                this.showToast('Ошибка запуска сценария')
                return false
            }

            if (data.diagnostics) {
                this.doctorMetrics = mapDiagnosticsToDoctorMetrics(data.diagnostics)
            }

            if (scenario.action === 'copyActivation') {
                await this.refreshCommunicationCalculations(projectId)
            }

            this.showToast('Сценарий запущен')
            await this.loadScenarios({ fallbackToMock: false })
            return true
        },
        async refreshCommunicationCalculations(projectId) {
            this.$store.commit('setCommunicationUpdate', true)
            await communicationUpdate({ projectId })
        },
        async disableScenario(scenario) {
            const projectId = this.getProjectId()
            if (!projectId || !isScenarioActive(scenario)) {
                return
            }

            if (STARTER_PACK_DISABLE_CONFIRM_MESSAGES[scenario.action]) {
                this.disableConfirmScenario = scenario
                return
            }

            await this.performDisableScenario(scenario)
        },
        closeDisableConfirmModal() {
            if (this.launching) {
                return
            }
            this.disableConfirmScenario = null
        },
        async confirmDisableScenario() {
            const scenario = this.disableConfirmScenario
            if (!scenario) {
                return
            }
            const success = await this.performDisableScenario(scenario)
            if (success) {
                this.disableConfirmScenario = null
            }
        },
        async performDisableScenario(scenario) {
            const projectId = this.getProjectId()
            if (!projectId || !isScenarioActive(scenario)) {
                return false
            }

            this.launching = true
            const { data, error } = await disableStarterPackScenario({
                projectId,
                scenarioId: scenario.scenarioId,
            })
            this.launching = false

            if (error || !data?.success) {
                this.$store.dispatch('callNotify', 'Не удалось отключить сценарий')
                return false
            }

            await this.loadScenarios({ fallbackToMock: false })
            this.showToast('Сценарий отключён')
            return true
        },
        closeDrawer() {
            if (this.drawerScenario?.meta?.settingsType === 'master15') {
                this.drawerScenario.settings.additionalServiceIds = this.startAdditionalServices.map(
                    service => service.id,
                )
            }
            this.drawerScenarioId = null
            this.selectedCommChannelId = null
            this.deauthVerifyingIndices = []
            this.deauthLoading = false
            this.master15ServicesLoading = false
        },
        addMaster(scenario, staffId) {
            if (!scenario?.settings?.masterIds || !staffId || scenario.settings.masterIds.includes(staffId)) {
                return
            }
            scenario.settings.masterIds.push(staffId)
        },
        toggleEmployeeMenu(scenarioId) {
            this.openEmployeeMenuScenarioId =
                this.openEmployeeMenuScenarioId === scenarioId ? null : scenarioId
        },
        closeEmployeeMenu() {
            this.openEmployeeMenuScenarioId = null
        },
        pickEmployee(scenario, staffId) {
            if (!scenario) {
                return
            }
            this.addMaster(scenario, staffId)
            this.closeEmployeeMenu()
        },
        removeMaster(scenario, staffId) {
            if (!scenario?.settings?.masterIds) {
                return
            }
            scenario.settings.masterIds = scenario.settings.masterIds.filter(id => id !== staffId)
        },
        onCommPhonesUpdated({ connectorId, phones }) {
            this.commControlPhonesByConnector = {
                ...this.commControlPhonesByConnector,
                [connectorId]: phones || [],
            }
        },
        async loadCommControlPhones(connectorId) {
            if (!connectorId) {
                return
            }
            try {
                const res = await controlPhoneList({ connectorId })
                if (!res?.error) {
                    this.commControlPhonesByConnector = {
                        ...this.commControlPhonesByConnector,
                        [connectorId]: res?.data || [],
                    }
                }
            } catch (e) {
                // controlPhoneList не поддерживается для части коннекторов
            }
        },
        async loadAllCommControlPhones() {
            await Promise.all(this.commChannels.map(channel => this.loadCommControlPhones(channel.id)))
        },
        collectAllCommPhonesForSettings() {
            const phones = []
            this.commChannels.forEach(channel => {
                const list = this.commControlPhonesByConnector[channel.id] || []
                list.forEach(item => {
                    phones.push({
                        phone: item.userId,
                        channelId: channel.id,
                        channelName: channel.displayName,
                    })
                })
            })
            return phones
        },
        selectCommChannel(channelId) {
            this.selectedCommChannelId = channelId
        },
        ensureSelectedCommChannel() {
            if (
                this.selectedCommChannelId &&
                this.commChannels.some(channel => channel.id === this.selectedCommChannelId)
            ) {
                return
            }
            this.selectedCommChannelId = this.commChannels[0]?.id || null
        },
        showToast(message) {
            this.toastMessage = message
            clearTimeout(this.toastTimer)
            this.toastTimer = setTimeout(() => {
                this.toastMessage = ''
            }, 2200)
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/vars.sass';

.content-wrapper {
    width: 100%;
    max-width: 1920px;
    min-width: 0;
    margin-left: auto;
    margin-right: auto;
}

.content {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
}

.starter-pack-page {
    max-width: 1010px;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    color: $text-color-new;
    margin: 0 auto;
    margin-top: 16px;
    font-family: Inter, system-ui, sans-serif;
}

.starter-pack-dynamic {
    position: relative;
}

@media (max-width: 1024px) {
    .content {
        padding-left: 20px;
        padding-right: 20px;
    }
}

@media (max-width: 760px) {
    .content {
        padding-left: 16px;
        padding-right: 16px;
    }
}
</style>
